/**
 * Согласование обновления токена между вкладками — БЕЗ обмена самими токенами.
 *
 * ЗАЧЕМ. `/auth/refresh` на бэкенде — ротация: первый запрос забирает
 * refresh-токен под себя, выдаёт новый и отзывает старый (AU6, ADR-007). Вторая
 * вкладка, пришедшая с тем же значением cookie, получает 401
 * `REFRESH_ROTATION_RACE`; бэкенд при этом СОХРАНЯЕТ семью токенов и прямо
 * просит повторить запрос (`auth.service.ts`, ветка `lostRotationRace`).
 *
 * ПОЧЕМУ НЕЛЬЗЯ ПРОСТО ПОВТОРИТЬ ПО ТАЙМЕРУ. Повтор со СТАРОЙ cookie попадает на
 * бэкенде уже не в гонку, а в `family_compromise` — отзыв ВСЕЙ семьи, то есть
 * разлогин на всех устройствах вместо одной вкладки. Признак «гонка» там
 * держится ровно столько, сколько идёт запрос победителя, и расширить его окном
 * времени нельзя: то же окно погасило бы обнаружение реюза (соседний тест
 * повторяет отозванный токен через единицы миллисекунд). Значит повторять можно
 * ТОЛЬКО тогда, когда новая cookie уже точно лежит в браузере.
 *
 * ПОЧЕМУ ЭТО РАБОТАЕТ. Cookie общая на все вкладки одного браузера. Вкладка,
 * которая обновление ВЫИГРАЛА, получает `Set-Cookie` вместе с ответом — и сразу
 * после этого объявляет о факте здесь. Проигравшая ждёт объявления и повторяет
 * запрос уже с новым значением. Наружу уходит только отметка времени: ни
 * access-, ни refresh-токен через этот канал не передаётся, инвариант «токен
 * живёт в памяти вкладки, refresh — HttpOnly» не нарушается.
 *
 * ГРАНИЦЫ. Канал существует в пределах одного браузера и одного источника.
 * Разные устройства и разные браузеры не согласуются никак — там проигравший
 * честно уйдёт на форму входа, и это правильное поведение: у него нет способа
 * узнать, что чужая сессия жива.
 */

/** Имя канала и ключа хранилища — одно на приложение. */
const CHANNEL_NAME = "auth-refresh";
const STORAGE_KEY = "auth:refreshed-at";

type Listener = () => void;

/** Слушатели, ожидающие объявления прямо сейчас. */
const waiters = new Set<Listener>();

function createChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    // Приватные режимы некоторых браузеров запрещают канал — работаем на запасном пути.
    return null;
  }
}

const channel = createChannel();

if (channel) {
  channel.onmessage = () => {
    notifyWaiters();
  };
}

/**
 * Запасной путь для браузеров без BroadcastChannel: событие `storage` приходит в
 * ДРУГИЕ вкладки того же источника. В своей вкладке оно не срабатывает — это и
 * нужно: своя вкладка о собственном успехе знает и без канала.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) notifyWaiters();
  });
}

function notifyWaiters(): void {
  for (const listener of Array.from(waiters)) listener();
}

/**
 * Объявить, что обновление прошло и новая cookie уже в браузере.
 * Вызывается ТОЛЬКО после успешного ответа: до него объявлять нечего.
 */
export function announceRefreshed(): void {
  const stamp = String(Date.now());
  try {
    channel?.postMessage(stamp);
  } catch {
    // Канал мог закрыться вместе с выгрузкой страницы — не повод падать.
  }
  try {
    // Запись нужна и как запасной канал, и как след для вкладки, которая
    // откроется позже: она увидит отметку и не станет ждать впустую.
    window.localStorage?.setItem(STORAGE_KEY, stamp);
  } catch {
    // Хранилище может быть недоступно (приватный режим, запрет куки) — тогда
    // остаётся только BroadcastChannel, а при его отсутствии ожидание истечёт
    // по таймауту, и это честный исход.
  }
}

/**
 * Дождаться объявления от другой вкладки.
 *
 * @returns `true` — объявление получено, повторять запрос безопасно;
 *          `false` — истёк срок ожидания, повторять НЕЛЬЗЯ (см. про
 *          `family_compromise` в шапке файла).
 */
export function waitForRefreshAnnouncement(
  timeoutMs: number,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;

    const finish = (result: boolean) => {
      if (settled) return;
      settled = true;
      waiters.delete(listener);
      clearTimeout(timer);
      resolve(result);
    };

    const listener: Listener = () => finish(true);
    waiters.add(listener);

    const timer = setTimeout(() => finish(false), timeoutMs);
  });
}
